import { getPubSubClient, eventToAvi } from '@confluence/pubsub-client';
import type { AddCommentType } from '@confluence/comment-context';

import { updateComment, reloadAndAddPageComments } from './pageCommentsOperations';
import type { PageCommentsReloadFn } from './pageCommentsOperations';

type OnPubSubEventFn = (input: {
	userId: string | null;
	reloader?: PageCommentsReloadFn;
	addNewComment?: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment?: (commentId?: string) => void;
	updateQueryVariables?: {
		contentId: string;
		isReactionsEnabled: boolean;
	};
}) => (event: string, payload: any) => void;

type PubSubParams = {
	contentId: string;
	contentType?: string | null;
	userId: string | null;
	reloader: PageCommentsReloadFn;
	addNewComment: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment: (commentId?: string) => void;
	queryArgs: {
		isReactionsEnabled: boolean;
	};
};

const onEvent: OnPubSubEventFn =
	({ userId, reloader, addNewComment, removeComment, updateQueryVariables }) =>
	(event, payload) => {
		const { commentRequestBody, accountId } = payload;
		const { commentId } = commentRequestBody;

		// We don't need to respond to events that the current user has initiated
		if (userId === accountId) {
			return;
		}

		switch (event) {
			case 'avi:confluence:created:comment':
				void reloadAndAddPageComments(
					reloader!,
					addNewComment!,
					[
						{
							commentId,
							accountId,
						},
					],
					userId,
				);
				break;
			case 'avi:confluence:deleted:comment':
				reloader &&
					reloader()
						.then(() => {
							removeComment && removeComment(commentId);
						})
						.catch((err: Error) => {
							throw err;
						});
				break;
			case 'avi:confluence:updated:comment':
				void updateComment(commentId, updateQueryVariables!);
				break;
			default:
				break;
		}
	};

export const setUpPageCommentsPubSub = ({
	contentId,
	contentType,
	userId,
	reloader,
	addNewComment,
	removeComment,
	queryArgs: { isReactionsEnabled },
}: PubSubParams) => {
	const pubSubResourceType = contentType || 'page';
	void getPubSubClient().then((client) => {
		client
			.joinChannel(pubSubResourceType, contentId)
			.finally(() => {
				void client.subscribe(
					eventToAvi('created:comment'),
					onEvent({ userId, reloader, addNewComment }),
				);
				void client.subscribe(
					eventToAvi('deleted:comment'),
					onEvent({ userId, reloader, removeComment }),
				);
				void client.subscribe(
					eventToAvi('updated:comment'),
					onEvent({
						userId,
						updateQueryVariables: {
							contentId,
							isReactionsEnabled,
						},
					}),
				);
			})
			.catch((error) => {
				throw new Error(`Unable to joinChannel ${JSON.stringify(error)}`);
			});
	});
};

export const removePageCommentsPubSub = ({
	contentId,
	contentType,
	userId,
	reloader,
	addNewComment,
	removeComment,
	queryArgs: { isReactionsEnabled },
}: PubSubParams) => {
	const pubSubResourceType = contentType || 'page';

	void getPubSubClient().then((client) => {
		void client.leaveChannel(pubSubResourceType, contentId);

		void client.unsubscribe(
			eventToAvi('created:comment'),
			onEvent({ userId, reloader, addNewComment }),
		);
		void client.unsubscribe(
			eventToAvi('deleted:comment'),
			onEvent({ userId, reloader, removeComment }),
		);
		void client.unsubscribe(
			eventToAvi('updated:comment'),
			onEvent({
				userId,
				updateQueryVariables: {
					contentId,
					isReactionsEnabled,
				},
			}),
		);
	});
};
